import React, { useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { AccountingAccountDA } from '../../services/admin/accounting.account.service';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { AccountingAccount } from '../../models/accounting.account.model';
import { GlobalSwal } from '../../styles/swal.style';
import { modalHide } from '../../utils/modal.util';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

export interface AppModalAccountCreateProps {
  companyForUser: Company | null,
  mode: 'sibling' | 'child',
  sourceAccount: AccountingAccount | null,
  parentAccount: AccountingAccount | null,
  loadFunction: Function
};

let errorResponse: Error;

const AppModalAccountCreate: React.FunctionComponent<AppModalAccountCreateProps> = ({companyForUser, mode, sourceAccount, parentAccount, loadFunction}) => {
  const {lang} = StateLanguage()

  const defaultCurrency = companyForUser?.currency?.code ?? 'CHF'

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [code, setCode] = useState('')
  const [nameFr, setNameFr] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [nameDe, setNameDe] = useState('')
  const [nameIt, setNameIt] = useState('')
  const [currency, setCurrency] = useState(defaultCurrency)

  const effectiveParent = mode === 'sibling' ? parentAccount : sourceAccount

  useEffect(() => {
    if (effectiveParent && companyForUser) {
      AccountingAccountDA.getNextCode(companyForUser.id, effectiveParent.id).then((response) => {
        if (response.status === 200) {
          setCode(response.data?.next_code ?? '')
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveParent, companyForUser])

  const submitAccountCreate = async () => {
    setLoadIndicator('on')

    if (!companyForUser || !effectiveParent || !code.trim() || !nameFr.trim()) {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then(() => {
        setLoadIndicator('off')
      })
      return
    }

    await AccountingAccountDA.postAccountingAccount(
      companyForUser.id,
      effectiveParent.id,
      'leave',
      code,
      { fr: nameFr, en: nameEn, de: nameDe, it: nameIt },
      currency,
      mode === 'sibling' && sourceAccount ? sourceAccount.id : undefined
    ).then((response) => {
      if (response.status === 201) {
        loadFunction(companyForUser.id)

        GlobalSwal.fire({
          title: lang.labels.accountCreatedSuccessfully,
          text: lang.labels.actionCompletedReturningToPage,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200
        }).then(() => {
          executeHideModal()
        })
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    }).finally(() => {
      setLoadIndicator('off')
    })
  }

  const executeHideModal = () => {
    modalHide('modal-account-create')

    setTimeout(() => {
      setCode('')
      setNameFr('')
      setNameEn('')
      setNameDe('')
      setNameIt('')
      setCurrency(defaultCurrency)
    }, 200)
  }

  return (
    <div id="modal-account-create" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{mode === 'sibling' ? lang.labels.createSiblingAccount : lang.labels.createChildAccount}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              { effectiveParent &&
                <div className="mb-5">
                  <span className="fw-bold text-muted">
                    {mode === 'sibling' ? lang.labels.siblingOf : lang.labels.subAccountOf}:
                  </span>
                  <span className="ms-2 fw-bolder">{sourceAccount?.code} - {sourceAccount?.name[(companyForUser?.language ?? 'fr').toLowerCase() as keyof typeof sourceAccount.name]}</span>
                </div>
              }
              <div className="row mb-4">
                <label className="col-lg-3 col-form-label required">{lang.labels.code}</label>
                <div className="col-lg-9">
                  <input className="form-control" type="text" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} />
                  <div className="form-text text-muted">{mode === 'sibling' ? lang.labels.siblingCodeHint : lang.labels.childCodeHint}</div>
                </div>
              </div>
              <div className="row mb-4">
                <label className="col-lg-3 col-form-label required">{lang.labels.nameFr}</label>
                <div className="col-lg-9">
                  <input className="form-control" type="text" autoComplete="off" value={nameFr} onChange={(e) => setNameFr(e.target.value)} />
                </div>
              </div>
              <div className="row mb-4">
                <label className="col-lg-3 col-form-label">{lang.labels.nameEn}</label>
                <div className="col-lg-9">
                  <input className="form-control" type="text" autoComplete="off" value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
                </div>
              </div>
              <div className="row mb-4">
                <label className="col-lg-3 col-form-label">{lang.labels.nameDe}</label>
                <div className="col-lg-9">
                  <input className="form-control" type="text" autoComplete="off" value={nameDe} onChange={(e) => setNameDe(e.target.value)} />
                </div>
              </div>
              <div className="row mb-4">
                <label className="col-lg-3 col-form-label">{lang.labels.nameIt}</label>
                <div className="col-lg-9">
                  <input className="form-control" type="text" autoComplete="off" value={nameIt} onChange={(e) => setNameIt(e.target.value)} />
                </div>
              </div>
              <div className="row mb-4">
                <label className="col-lg-3 col-form-label">{lang.labels.currency}</label>
                <div className="col-lg-9">
                  <select className="form-select" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                    <option value={defaultCurrency}>{defaultCurrency}</option>
                    {defaultCurrency !== 'CHF' && <option value="CHF">CHF</option>}
                    {defaultCurrency !== 'EUR' && <option value="EUR">EUR</option>}
                    {defaultCurrency !== 'USD' && <option value="USD">USD</option>}
                    {defaultCurrency !== 'GBP' && <option value="GBP">GBP</option>}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModal}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitAccountCreate}>
              <span className="indicator-label">{lang.labels.create}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalAccountCreate;
